import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, CircularProgress, Box } from '@mui/material';
import {
  ShoppingCart,
  Add,
  Visibility,
  Edit,
  LocalOffer,
  AttachMoney,
  Category,
  Inventory,
} from '@mui/icons-material';
import ServiceDashboard from '../../components/services/ServiceDashboard';
import api from '../../services/api';

const MarketplaceDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [serviceUser, setServiceUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<any>({
    stats: [],
    recentItems: [],
  });

  useEffect(() => {
    const serviceUserData = localStorage.getItem('serviceUserData');
    if (serviceUserData) {
      setServiceUser(JSON.parse(serviceUserData));
    }
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/marketplace/my-listings');
      const listings = response.data.listings || [];
      
      // Calculate stats
      const stats = [
        {
          label: t('marketplace.dashboard.totalListings'),
          value: listings.length,
          icon: <Inventory sx={{ color: 'primary.main' }} />,
        },
        {
          label: t('marketplace.dashboard.activeListings'),
          value: listings.filter((l: any) => l.status === 'active').length,
          icon: <LocalOffer sx={{ color: 'success.main' }} />,
          color: 'success.main',
        },
        {
          label: t('marketplace.dashboard.totalViews'),
          value: listings.reduce((sum: number, l: any) => sum + (l.views || 0), 0),
          icon: <Visibility sx={{ color: 'info.main' }} />,
        },
        {
          label: t('marketplace.dashboard.soldItems'),
          value: listings.filter((l: any) => l.status === 'sold').length,
          icon: <AttachMoney sx={{ color: 'warning.main' }} />,
          color: 'warning.main',
        },
      ];

      // Format recent items
      const recentItems = listings.slice(0, 5).map((listing: any) => ({
        title: listing.title,
        subtitle: `${formatPrice(listing.price)} - ${listing.category || t('marketplace.search.noCategory')}`,
        status: listing.status,
        statusColor: getStatusColor(listing.status),
        actions: [
          {
            label: t('marketplace.dashboard.view'),
            icon: <Visibility />,
            onClick: () => navigate(`/app/marketplace/listing/${listing.id}`),
            variant: 'text' as const,
          },
          listing.status === 'active' && {
            label: t('marketplace.dashboard.edit'),
            icon: <Edit />,
            onClick: () => navigate(`/app/marketplace/edit/${listing.id}`),
            variant: 'text' as const,
          },
        ].filter(Boolean),
      }));

      setDashboardData({ stats, recentItems });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'sold':
        return 'info';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const getUserTier = () => {
    if (serviceUser?.has_premium_access || serviceUser?.service_tier === 'premium') {
      return 'premium';
    }
    return 'free';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const quickActions = [
    {
      label: t('marketplace.dashboard.postNewListing'),
      icon: <Add />,
      onClick: () => navigate('/app/marketplace/create'),
      variant: 'contained' as const,
    },
    {
      label: t('marketplace.dashboard.browseListings'),
      icon: <ShoppingCart />,
      onClick: () => navigate('/app/marketplace'),
      variant: 'outlined' as const,
    },
    {
      label: t('marketplace.dashboard.manageListings'),
      icon: <Category />,
      onClick: () => navigate('/app/marketplace/my-listings'),
      variant: 'outlined' as const,
    },
  ];

  const features = [
    {
      title: t('marketplace.features.postListings'),
      description: t('marketplace.features.postListingsDesc'),
      available: true,
    },
    {
      title: t('marketplace.features.uploadPhotos'),
      description: t('marketplace.features.uploadPhotosDesc'),
      available: getUserTier() === 'premium',
    },
    {
      title: t('marketplace.features.premiumPlacement'),
      description: t('marketplace.features.premiumPlacementDesc'),
      available: getUserTier() === 'premium',
    },
    {
      title: t('marketplace.features.analytics'),
      description: t('marketplace.features.analyticsDesc'),
      available: getUserTier() === 'premium',
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <ServiceDashboard
        serviceId="marketplace"
        stats={dashboardData.stats}
        recentItems={dashboardData.recentItems}
        quickActions={quickActions}
        features={features}
        userTier={getUserTier()}
        upgradeMessage={t('marketplace.dashboard.upgradeMessage')}
        emptyStateMessage={t('marketplace.dashboard.noListings')}
        emptyStateAction={{
          label: t('marketplace.createListing'),
          onClick: () => navigate('/app/marketplace/create'),
        }}
      />
    </Container>
  );
};

export default MarketplaceDashboard;